import { Injectable, inject, signal, computed, Injector, runInInjectionContext } from '@angular/core';
import {
  Auth,
  signInWithEmailAndPassword,
  signOut,
  authState,
  sendPasswordResetEmail
} from '@angular/fire/auth';
import { Firestore, doc, docData } from '@angular/fire/firestore';
import { Router } from '@angular/router';
import { User, UserRole } from '../models/user.model';
import { UserService } from './user.service';
import { Subscription } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private auth = inject(Auth);
  private firestore = inject(Firestore);
  private router = inject(Router);
  private userService = inject(UserService);
  private injector = inject(Injector);
  private profileSyncSub: Subscription | null = null;
  private profileSyncUid: string | null = null;

  // Signals for reactive state management
  currentUser = signal<User | null>(null);
  isLoading = signal(false);
  errorMessage = signal<string | null>(null);
  isAuthenticated = signal(false);

  // Firebase auth state observable
  authState$ = authState(this.auth);

  // Role helpers — Minister and OSD are distinct roles with the same write authority.
  isAdmin    = computed(() => this.currentUser()?.role === UserRole.ADMIN);
  isOperator = computed(() => this.currentUser()?.role === UserRole.OPERATOR);
  isApprover = computed(() => this.currentUser()?.role === UserRole.APPROVER);
  isMinister = computed(() => this.currentUser()?.role === UserRole.MINISTER);
  isOsd      = computed(() => this.currentUser()?.role === UserRole.OSD);
  /** Combined helper — both roles unlock the super-dashboard / direct-write path. */
  isMinisterOrOsd = computed(() => this.isMinister() || this.isOsd());

  constructor() {
    // Subscribe to auth state changes
    this.authState$.subscribe(async (firebaseUser) => {
      if (firebaseUser) {
        this.isAuthenticated.set(true);
        try {
          const userData = await this.resolveUserProfile(firebaseUser.uid, firebaseUser.email ?? '');
          if (userData) {
            // Cache role so it survives offline sessions
            localStorage.setItem(`ppms_role_${firebaseUser.uid}`, userData.role);
            this.currentUser.set(userData);
            this.startRealtimeProfileSync(userData.uid, firebaseUser.uid);
          } else {
            // Doc doesn't exist yet — auto-provision Firestore profile on first login
            const created = await runInInjectionContext(this.injector, () =>
              this.userService.createSelfProfile(firebaseUser)
            );
            localStorage.setItem(`ppms_role_${firebaseUser.uid}`, created.role);
            this.currentUser.set(created);
            this.startRealtimeProfileSync(created.uid, firebaseUser.uid);
          }
        } catch (error) {
          // Firestore offline — restore role from localStorage cache so save/edit buttons remain enabled
          console.warn('Firestore offline — using cached role for fallback. Error:', error);
          const cachedRole = localStorage.getItem(`ppms_role_${firebaseUser.uid}`) ?? 'viewer';
          
          // Check if we have a valid cached role from previous sessions
          if (cachedRole && cachedRole !== 'viewer') {
            this.currentUser.set({
              uid: firebaseUser.uid,
              email: firebaseUser.email ?? '',
              displayName: firebaseUser.displayName ?? firebaseUser.email?.split('@')[0] ?? 'User',
              role: cachedRole as any,
              createdAt: new Date(),
              isActive: true,
            } as any);
          } else {
            // No valid cached role - try to infer from email for basic access
            const inferredRole = this.inferRoleFromEmail(firebaseUser.email || '');
            this.currentUser.set({
              uid: firebaseUser.uid,
              email: firebaseUser.email ?? '',
              displayName: firebaseUser.displayName ?? firebaseUser.email?.split('@')[0] ?? 'User',
              role: inferredRole as UserRole,
              createdAt: new Date(),
              isActive: true,
            });
          }
          
          this.scheduleFirestoreRetry(firebaseUser.uid, firebaseUser.email ?? '');
        }
      } else {
        this.stopRealtimeProfileSync();
        this.isAuthenticated.set(false);
        this.currentUser.set(null);
      }
    });
  }

  private startRealtimeProfileSync(profileUid: string, firebaseUidForCache: string): void {
    if (!profileUid) return;
    if (this.profileSyncUid === profileUid && this.profileSyncSub) return;
    this.stopRealtimeProfileSync();
    this.profileSyncUid = profileUid;

    const profileDoc = doc(this.firestore, 'users', profileUid);
    this.profileSyncSub = runInInjectionContext(this.injector, () => docData(profileDoc))
      .subscribe({
        next: async () => {
          try {
            const latest = await runInInjectionContext(this.injector, () =>
              this.userService.getUserByUid(profileUid)
            );
            if (latest) {
              localStorage.setItem(`ppms_role_${firebaseUidForCache}`, latest.role);
              this.currentUser.set(latest);
              this.isAuthenticated.set(true);
            }
          } catch (err) {
            console.warn('[auth] realtime profile sync read failed:', err);
          }
        },
        error: (err) => {
          console.warn('[auth] realtime profile sync stream failed:', err);
        }
      });
  }

  private stopRealtimeProfileSync(): void {
    this.profileSyncSub?.unsubscribe();
    this.profileSyncSub = null;
    this.profileSyncUid = null;
  }

  /**
   * Resolve profile primarily by Firebase UID, then fallback to email.
   * This protects operator/admin sessions when legacy user docs were saved
   * under a different document id than the current auth uid.
   */
  private async resolveUserProfile(uid: string, email: string): Promise<User | null> {
    const byUid = await runInInjectionContext(this.injector, () =>
      this.userService.getUserByUid(uid)
    );
    if (byUid) {
      if (byUid.uid !== uid) {
        return runInInjectionContext(this.injector, () =>
          this.userService.mirrorProfileToAuthUid(uid, byUid)
        );
      }
      return byUid;
    }

    const lookupEmail = this.userService.normalizeEmail(email) || email.trim();
    if (!lookupEmail) return null;

    const byEmail = await runInInjectionContext(this.injector, () =>
      this.userService.getUserByEmail(lookupEmail)
    );
    if (!byEmail) return null;

    if (byEmail.uid !== uid) {
      try {
        return await runInInjectionContext(this.injector, () =>
          this.userService.mirrorProfileToAuthUid(uid, byEmail)
        );
      } catch (err) {
        console.warn('[auth] mirrorProfileToAuthUid failed:', err);
        return byEmail;
      }
    }

    return byEmail;
  }

  /** Retry loading full user profile from Firestore after a delay (handles offline startup) */
  private scheduleFirestoreRetry(uid: string, email = '', attempt = 1): void {
    const delayMs = Math.min(attempt * 3000, 15000); // 3s, 6s … max 15s
    setTimeout(() => {
      this.resolveUserProfile(uid, email)
        .then(userData => {
          if (userData) {
            console.log(`✅ Successfully loaded user profile for ${uid}, role: ${userData.role}`);
            localStorage.setItem(`ppms_role_${uid}`, userData.role);
            this.currentUser.set(userData);
            this.startRealtimeProfileSync(userData.uid, uid);
          } else if (attempt < 5) {
            console.warn(`⚠️ User profile not found, retrying attempt ${attempt}/5 for ${uid}`);
            this.scheduleFirestoreRetry(uid, email, attempt + 1);
          } else {
            console.error(`❌ Failed to load user profile after 5 attempts for ${uid}`);
          }
        })
        .catch((error) => {
          console.error(`❌ Error loading user profile for ${uid}:`, error);
          if (attempt < 5) {
            this.scheduleFirestoreRetry(uid, email, attempt + 1);
          } else {
            console.error(`❌ Failed to load user profile after 5 attempts for ${uid}`);
          }
        });
    }, delayMs);
  }

  /**
   * Best-effort role inference for seed accounts when the Firestore
   * profile lookup fails (e.g. rules not yet deployed).
   */
  private inferRoleFromEmail(email: string): string {
    const e = (email || '').toLowerCase().trim();
    if (e === 'minister@nimbletech.co.in' || e.includes('minister')) return 'minister';
    if (e === 'osd@email.com' || e.includes('osd'))                  return 'osd';
    if (e.includes('admin'))                                         return 'admin';
    if (e.startsWith('test_op') || e.includes('operator'))           return 'operator';
    if (e.startsWith('test_ap') || e.includes('approver'))           return 'approver';
    return 'operator';
  }

  /**
   * Sign in with email and password
   */
  async login(email: string, password: string): Promise<void> {
    this.isLoading.set(true);
    this.errorMessage.set(null);

    const normalizedEmail = this.userService.normalizeEmail(email);
    const pwd = String(password ?? '');

    try {
      console.log('Attempting login for:', normalizedEmail);
      let userCredential;
      try {
        userCredential = await signInWithEmailAndPassword(this.auth, normalizedEmail, pwd);
      } catch (signInErr: any) {
        if (signInErr.code === 'auth/invalid-credential') {
          console.log(`[auth] Account not found, attempting auto-seed for: ${normalizedEmail}`);
          const uid = await this.userService.autoCreateTestAccount(normalizedEmail, pwd);
          if (!uid) {
            throw signInErr;
          }
          userCredential = await signInWithEmailAndPassword(this.auth, normalizedEmail, pwd);
        } else {
          throw signInErr;
        }
      }
      console.log('Login successful, user UID:', userCredential.user.uid);

      const authEmail = userCredential.user.email ?? normalizedEmail;
      let userData: User | null = null;
      try {
        userData = await this.resolveUserProfile(userCredential.user.uid, authEmail);
      } catch (profileErr) {
        console.warn('[auth] Could not fetch user profile — continuing with fallback role.', profileErr);
      }
      console.log('User Role:', userData?.role);

      if (userData?.isActive === false) {
        await signOut(this.auth);
        this.currentUser.set(null);
        this.isAuthenticated.set(false);
        this.errorMessage.set('This account has been deactivated. Contact your administrator.');
        this.isLoading.set(false);
        throw { code: 'auth/user-disabled' };
      }

      this.userService.updateLastLogin(userCredential.user.uid).catch(error => {
        console.warn('Failed to update last login:', error);
      });

      const role = userData?.role ?? this.inferRoleFromEmail(authEmail);

      if (userData) {
        this.currentUser.set(userData);
        this.startRealtimeProfileSync(userCredential.user.uid, userCredential.user.uid);
      } else {
        this.currentUser.set({
          uid: userCredential.user.uid,
          email: authEmail,
          displayName: userCredential.user.displayName ?? authEmail.split('@')[0],
          role: role as UserRole,
          createdAt: new Date(),
          isActive: true,
        } as User);
      }
      this.isAuthenticated.set(true);
      localStorage.setItem(`ppms_role_${userCredential.user.uid}`, role);

      const landing =
        role === UserRole.ADMIN    ? '/dhamnagar-dashboard/user-management'
      : role === UserRole.APPROVER ? '/dhamnagar-dashboard/approver'
      : role === UserRole.OPERATOR ? '/dhamnagar-dashboard/operator'
      : (role === UserRole.OSD || role === UserRole.MINISTER) ? '/home'
      :                              '/home';
      console.log(`Navigating to ${landing} (role=${role})...`);
      const navigated = await this.router.navigate([landing]);
      console.log('Navigation result:', navigated);
      this.isLoading.set(false);
    } catch (error: any) {
      console.error('Login error:', error);
      this.isLoading.set(false);
      
      // Set user-friendly error message
      switch (error.code) {
        case 'auth/invalid-email':
          this.errorMessage.set('Invalid email address.');
          break;
        case 'auth/user-disabled':
          this.errorMessage.set('This account has been deactivated. Contact your administrator.');
          break;
        case 'auth/user-not-found':
        case 'auth/wrong-password':
          this.errorMessage.set('Invalid email or password.');
          break;
        case 'auth/invalid-credential':
          this.errorMessage.set('Invalid credentials. Please check your email and password.');
          break;
        default:
          this.errorMessage.set('An error occurred during login. Please try again.');
      }
      
      throw error;
    }
  }

  /**
   * Sign out the current user
   */
  async logout(): Promise<void> {
    try {
      console.log('Logging out user...');
      
      // Reset all signals immediately to prevent race conditions
      this.isLoading.set(false);
      this.errorMessage.set(null);
      this.currentUser.set(null);
      this.isAuthenticated.set(false);
      this.stopRealtimeProfileSync();
      
      // Sign out from Firebase
      await signOut(this.auth);
      console.log('Signed out from Firebase');
      
      // Navigate to login page
      await this.router.navigate(['/login']);
      console.log('Navigated to login page');
    } catch (error) {
      console.error('Logout error:', error);
      // Still clear auth state even if logout fails
      this.isLoading.set(false);
      this.currentUser.set(null);
      this.isAuthenticated.set(false);
      this.stopRealtimeProfileSync();
      throw error;
    }
  }

  /**
   * Send password reset email
   */
  async resetPassword(email: string): Promise<void> {
    try {
      console.log('Sending password reset email to:', email);
      await sendPasswordResetEmail(this.auth, email);
    } catch (error: any) {
      console.error('Password reset error:', error);
      throw error;
    }
  }

  /**
   * Get current user
   */
  getCurrentUser(): User | null {
    return this.currentUser();
  }

  /**
   * Raw Firebase Auth UID for current session.
   * Used where legacy mappings may still reference auth uid directly.
   */
  getFirebaseAuthUid(): string | null {
    return this.auth.currentUser?.uid ?? null;
  }
}
